import EyeOnSvg from '@/assets/icons/eye.svg?react';
import EyeOffSvg from '@/assets/icons/eyeOff.svg?react';
import { formatTickDate, formatTickTime } from '@/components/PowerChart/utils';
import {
  TurbineHistorySeriesMetric,
  TurbineSeriesError
} from '@/hooks/services/Turbines/useTurbineHistorySeries';
import { pulseChartColors } from '@/solaris-lens-common-lib/src/constants/colors';

import styles from './styles.module.css';

export type DeltaMap = Record<string, number>;

export interface PowerChartDeltaDataPoint {
  timestamp: string;
  [key: string]: string | number;
}

export interface PowerChartDeltaProps {
  data: PowerChartDeltaDataPoint[];
  isLoading?: boolean;
  onCursorChange?: (
    blueCursor: PowerChartDeltaDataPoint | null,
    redCursor: PowerChartDeltaDataPoint | null,
    delta: DeltaMap | null
  ) => void;
  chartMetrics: TurbineHistorySeriesMetric[];
  onRemoveMetric?: (metric: TurbineHistorySeriesMetric) => void;
  errors?: TurbineSeriesError[];
  onNavigateBack?: () => void;
  onNavigateForward?: () => void;
  canNavigateForward?: boolean;
  navigateBackLabel?: string;
  navigateForwardLabel?: string;
}

export const PANEL_LAYOUT = {
  ROW_HEIGHT: 40,
  LEGEND_HEADER: 40,
  CURSOR_TABLE: 84,
  FOOTER_BTN: 42,
  MARGINS: 16,
  CHART_MIN_HEIGHT: 420
};

export const getKeyColor = (key: string): string => {
  const hash = key.split('').reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  return pulseChartColors[hash % pulseChartColors.length];
};

export const calcRootMinHeight = (numKeys: number): number => {
  const {
    ROW_HEIGHT,
    LEGEND_HEADER,
    CURSOR_TABLE,
    FOOTER_BTN,
    MARGINS,
    CHART_MIN_HEIGHT
  } = PANEL_LAYOUT;
  const panelHeight =
    LEGEND_HEADER + ROW_HEIGHT * numKeys + CURSOR_TABLE + FOOTER_BTN + MARGINS;
  return Math.max(CHART_MIN_HEIGHT, panelHeight);
};

export const formatValue = (
  val: string | number | undefined | null
): string => {
  if (val === undefined || val === null) return '—';
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(num)) return '—';
  return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

export const formatDuration = (ms: number): string => {
  const totalSec = Math.floor(Math.abs(ms) / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

export const tsFromData = (
  data: PowerChartDeltaDataPoint[],
  idx: number | null
): number => {
  if (idx === null) return NaN;
  return new Date(String(data[idx]?.timestamp)).getTime();
};

export const computeYTicks = (
  data: PowerChartDeltaDataPoint[],
  dataKeys: string[]
): number[] => {
  if (!data.length || !dataKeys.length) return [];
  let min = Infinity;
  let max = -Infinity;
  data.forEach(point => {
    dataKeys.forEach(key => {
      const val = parseFloat(String(point[key]));
      if (!isNaN(val)) {
        min = Math.min(min, val);
        max = Math.max(max, val);
      }
    });
  });
  if (!isFinite(min) || !isFinite(max)) return [];
  const step = (max - min) / 5;
  return Array.from({ length: 6 }, (_, i) =>
    parseFloat((min + step * i).toFixed(2))
  );
};

export const computeDelta = (
  bluePoint: PowerChartDeltaDataPoint,
  redPoint: PowerChartDeltaDataPoint,
  dataKeys: string[]
): DeltaMap => {
  const result: DeltaMap = {};
  dataKeys.forEach(key => {
    const bVal = parseFloat(String(bluePoint[key] ?? 0));
    const rVal = parseFloat(String(redPoint[key] ?? 0));
    result[key] = parseFloat((rVal - bVal).toFixed(2));
  });
  return result;
};

export const MAX_X_AXIS_TICKS = 6;

export const calcXAxisTicks = (
  data: PowerChartDeltaDataPoint[],
  maxTicks = MAX_X_AXIS_TICKS
): string[] => {
  if (!data.length) return [];
  if (data.length <= maxTicks) return data.map(point => String(point.timestamp));

  const lastIndex = data.length - 1;
  const ticks = Array.from({ length: maxTicks }, (_, i) => {
    const index = Math.round((i * lastIndex) / (maxTicks - 1));
    return String(data[index].timestamp);
  });

  return [...new Set(ticks)];
};

export const EyeOnIcon = () => <EyeOnSvg width="16" height="16" />;
export const EyeOffIcon = () => <EyeOffSvg width="16" height="16" />;

const getXTickTextAnchor = (index: number, visibleTicksCount: number) => {
  if (index === 0) return 'start';
  if (index === visibleTicksCount - 1) return 'end';
  return 'middle';
};

export const CustomXAxisTick = ({
  x,
  y,
  payload,
  index,
  visibleTicksCount
}: any) => {
  const textAnchor = getXTickTextAnchor(index, visibleTicksCount);

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor={textAnchor}
        className={styles.xTick}
      >
        {formatTickDate(payload.value)}
      </text>
      <text
        x={0}
        y={16}
        dy={16}
        textAnchor={textAnchor}
        className={styles.xTick}
      >
        {formatTickTime(payload.value)}
      </text>
    </g>
  );
};

export const CursorClearLabel = ({
  viewBox,
  color,
  onClear
}: {
  viewBox?: { x: number; y: number; height: number };
  color: string;
  onClear: () => void;
}) => {
  if (!viewBox) return null;
  const { x, y, height } = viewBox;
  const btnY = y + height - 18;
  return (
    <g>
      <rect
        x={x - 11}
        y={btnY}
        width={22}
        height={16}
        rx={3}
        fill="transparent"
        stroke={color}
        strokeWidth={1.5}
        style={{ cursor: 'pointer' }}
        onClick={e => {
          e.stopPropagation();
          onClear();
        }}
      />
      <text
        x={x}
        y={btnY + 12}
        textAnchor="middle"
        fill={color}
        fontSize={13}
        fontWeight="bold"
        style={{ cursor: 'pointer', pointerEvents: 'none' }}
      >
        —
      </text>
    </g>
  );
};
